import type { AxiosRequestConfig } from "axios";

import ClientBase from "@citrine/client";

import type {
  JobExecutionStatus,
  JobOutput,
  PollingOptions,
} from "../job-execution/job-execution";
import {
  JobExecutionError,
  JobTimeoutError,
} from "../job-execution/job-execution";

export class JobExecutionClient<
  Output extends JobOutput = JobOutput,
> extends ClientBase<JobExecutionStatus<Output>> {
  protected constructor(endpoint: string) {
    super({ endpoint });
  }

  static getInstanceForProject<Output extends JobOutput = JobOutput>(
    projectId: string
  ) {
    return new JobExecutionClient<Output>(
      `/api/v1/projects/${projectId}/execution/job-status`
    );
  }

  static getInstanceForTeam<Output extends JobOutput = JobOutput>(
    teamId: string
  ) {
    return new JobExecutionClient<Output>(
      `/api/v1/teams/${teamId}/execution/job-status`
    );
  }

  poll(
    id: string,
    { interval = 3000, timeout = 60000 }: PollingOptions = {}
  ): Promise<JobExecutionStatus<Output>> {
    return new Promise<JobExecutionStatus<Output>>((resolve, reject) => {
      let isTimedOut = false;
      const timeoutHandle = window.setTimeout(
        () => {
          isTimedOut = true;
        },
        Math.max(interval, timeout, 10000)
      );

      const intervalHandle = window.setInterval(
        async () => {
          try {
            const job = await this.get(id);
            switch (job.status) {
              case "Success":
                clear();
                resolve(job);
                return;
              case "Failure":
                throw new JobExecutionError(job);
            }
            if (isTimedOut) {
              throw new JobTimeoutError(job);
            }
          } catch (e) {
            clear();
            reject(e);
          }
        },
        Math.max(interval, 1000)
      );

      const clear = () => {
        window.clearTimeout(timeoutHandle);
        window.clearInterval(intervalHandle);
      };
    });
  }

  protected getConfig(jobId: string): AxiosRequestConfig {
    return {
      url: this.endpoint,
      method: "GET",
      params: {
        job_id: jobId,
      },
    };
  }
}
